import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Eye,
  Pencil,
  FileText,
  Copy,
  Check,
  Save,
  Loader2,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  useUpdateTaskMutation,
  useGetTaskChildrenQuery,
  useDeleteTaskMutation,
} from '@/store/api/apiSlice';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { renderMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import type { Task, TaskStatus } from '@/types/index';

// ─── Types ──────────────────────────────────────────────────────────────────

interface TaskDetailDrawerProps {
  task: Task | null;
  allTasks: Task[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddSubtask: (parentId: string) => void;
  onNavigateToParent: (parentId: string) => void;
  onStatusChanged?: (taskId: string) => void;
  onCascadeCompleteChildren?: (parentId: string, status: TaskStatus) => void;
}

// ─── Status config ──────────────────────────────────────────────────────────

const statusOptions: { value: TaskStatus; label: string; dotClass: string; badgeClass: string }[] = [
  {
    value: 'pending',
    label: 'Pending',
    dotClass: 'text-gray-400 dark:text-gray-500',
    badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    dotClass: 'text-blue-500 dark:text-blue-400',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  },
  {
    value: 'blocked',
    label: 'Blocked',
    dotClass: 'text-red-500 dark:text-red-400',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  },
  {
    value: 'completed',
    label: 'Completed',
    dotClass: 'text-green-500 dark:text-green-400',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    dotClass: 'text-gray-400 dark:text-gray-600',
    badgeClass: 'bg-gray-100 text-gray-500 line-through',
  },
];

const statusMap = new Map(statusOptions.map((s) => [s.value, s]));

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'PPpp');
  } catch {
    return dateStr;
  }
}

function formatRelative(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TaskDetailDrawer({
  task,
  allTasks,
  open,
  onOpenChange,
  onAddSubtask,
  onNavigateToParent,
  onStatusChanged,
  onCascadeCompleteChildren,
}: TaskDetailDrawerProps) {
  // ── RTK Query hooks ─────────────────────────────────────────────────────
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const {
    data: children = [],
    isLoading: isLoadingChildren,
    isError: isChildrenError,
  } = useGetTaskChildrenQuery(task?._id ?? '', { skip: !task });

  // ── Local state ─────────────────────────────────────────────────────────
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task?.title ?? '');
  const [descriptionDraft, setDescriptionDraft] = useState(task?.description ?? '');
  const [statusDraft, setStatusDraft] = useState<TaskStatus>(task?.status ?? 'pending');
  const [ownerIdDraft, setOwnerIdDraft] = useState(task?.ownerId ?? '');
  const [sourceDraft, setSourceDraft] = useState(task?.source ?? '');
  const [markdownDraft, setMarkdownDraft] = useState(task?.markdown ?? '');
  const [metadataDraft, setMetadataDraft] = useState(
    task?.metadata ? JSON.stringify(task.metadata, null, 2) : '',
  );
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<null | 'saving' | 'saved' | 'error'>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const [showDescriptionPreview, setShowDescriptionPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Refs ────────────────────────────────────────────────────────────────
  const titleInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Derived data ────────────────────────────────────────────────────────
  const parentTask = task?.parentId
    ? allTasks.find((t) => t._id === task.parentId)
    : null;

  const hasChildren = children.length > 0;
  const completedCount = children.filter((c) => c.status === 'completed').length;
  const progressPct = hasChildren ? Math.round((completedCount / children.length) * 100) : 0;

  const currentStatusCfg = task ? (statusMap.get(task.status) ?? statusOptions[0]) : statusOptions[0];

  // ── Title editing ───────────────────────────────────────────────────────
  const startEditingTitle = useCallback(() => {
    if (!task) return;
    setTitleDraft(task.title);
    setIsEditingTitle(true);
    // Focus after render
    requestAnimationFrame(() => titleInputRef.current?.focus());
  }, [task]);

  // ── Change handlers (local draft only) ──────────────────────────────────
  const handleTitleChange = (value: string) => {
    setTitleDraft(value);
  };

  const handleDescriptionChange = (value: string) => {
    setDescriptionDraft(value);
  };

  const handleStatusChange = (value: string) => {
    setStatusDraft(value as TaskStatus);
  };

  // ── Auto-resize textarea ────────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [descriptionDraft]);

  // ── hasChanges ────────────────────────────────────────────────────────────
  const hasChanges = useMemo(() => {
    if (!task) return false;
    return (
      titleDraft !== task.title ||
      descriptionDraft !== (task.description || '') ||
      statusDraft !== task.status ||
      ownerIdDraft !== task.ownerId ||
      sourceDraft !== (task.source || '') ||
      markdownDraft !== (task.markdown || '') ||
      metadataDraft !== (task.metadata ? JSON.stringify(task.metadata, null, 2) : '')
    );
  }, [titleDraft, descriptionDraft, statusDraft, ownerIdDraft, sourceDraft, markdownDraft, metadataDraft, task]);

  // ── Explicit save ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!task) return;
    
    // Validate metadata JSON
    let parsedMetadata: Record<string, any> | undefined;
    if (metadataDraft.trim()) {
      try {
        parsedMetadata = JSON.parse(metadataDraft);
        setMetadataError(null);
      } catch {
        setMetadataError('Invalid JSON');
        return;
      }
    }

    try {
      setSaveState('saving');
      await updateTask({
        id: task._id,
        body: {
          title: titleDraft,
          description: descriptionDraft,
          status: statusDraft,
          ownerId: ownerIdDraft,
          source: sourceDraft || undefined,
          markdown: markdownDraft || undefined,
          metadata: parsedMetadata,
        },
      }).unwrap();
      setSaveState('saved');
      setTimeout(() => setSaveState(null), 2000);

      // Cascade: complete all children when parent is completed
      if (statusDraft === 'completed' && task.status !== 'completed' && onCascadeCompleteChildren) {
        onCascadeCompleteChildren(task._id, statusDraft);
      }
      // Notify parent if status changed
      if (statusDraft !== task.status && onStatusChanged) {
        onStatusChanged(task._id);
      }
    } catch (err) {
      setSaveState('error');
      setTimeout(() => setSaveState(null), 3000);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = useCallback(() => {
    if (!task) return;
    deleteTask(task._id)
      .unwrap()
      .then(() => onOpenChange(false))
      .catch(() => setShowDeleteConfirm(false));
  }, [task, deleteTask, onOpenChange]);

  // ── Sync drafts when task changes (only when a different task is selected) ─
  useEffect(() => {
    if (task) {
      setTitleDraft(task.title);
      setDescriptionDraft(task.description ?? '');
      setStatusDraft(task.status);
      setOwnerIdDraft(task.ownerId);
      setSourceDraft(task.source ?? '');
      setMarkdownDraft(task.markdown ?? '');
      setMetadataDraft(task.metadata ? JSON.stringify(task.metadata, null, 2) : '');
      setIsEditingTitle(false);
    }
  }, [task?._id]);

  // ── Render ──────────────────────────────────────────────────────────────
  if (!task) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-[600px] w-full sm:w-1/2 min-w-0 sm:min-w-[360px] p-0 flex flex-col">
          <SheetHeader className="px-4 sm:px-6 py-4 border-b">
            <div className="flex items-start gap-2">
              {/* Title */}
              <div className="flex-1 min-w-0">
                {isEditingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={titleDraft}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    className="w-full rounded-md border border-input bg-background px-2 py-1 text-lg font-semibold leading-tight focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                ) : (
                  <SheetTitle
                    className="cursor-pointer truncate text-lg font-semibold leading-tight hover:text-muted-foreground"
                    onClick={startEditingTitle}
                    title="Click to edit"
                  >
                    {titleDraft}
                  </SheetTitle>
                )}
              </div>

              {/* Save button */}
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  disabled={saveState === 'saving'}
                  size="sm"
                  className="gap-2"
                >
                  {saveState === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : saveState === 'saved' ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save changes
                    </>
                  )}
                </Button>
              )}

              {/* Action buttons */}
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  aria-label="Delete task"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {/* ── Scrollable content ──────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 py-4">
              {/* ── Status dropdown ─────────────────────────────────────── */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-muted-foreground">
                  Status
                </label>
                <Select value={statusDraft} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[180px] h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <span
                            className={cn(
                              'inline-block h-2 w-2 rounded-full',
                              opt.dotClass,
                            )}
                          />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                    currentStatusCfg.badgeClass,
                  )}
                >
                  {currentStatusCfg.label}
                </span>
              </div>

              {/* ── Details (editable) ───────────────────────────────────── */}
              <div>
                <h3 className="mb-3 text-sm font-semibold text-foreground">Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  {/* Owner */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">Owner</label>
                    <input
                      type="text"
                      value={ownerIdDraft}
                      onChange={(e) => setOwnerIdDraft(e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  {/* Source */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">Source</label>
                    <input
                      type="text"
                      value={sourceDraft}
                      onChange={(e) => setSourceDraft(e.target.value)}
                      placeholder="Optional source"
                      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  {/* Depth (read-only) */}
                  <MetadataField label="Depth" value={String(task.depth)} />
                  {/* Created (read-only) */}
                  <MetadataField label="Created" value={formatDate(task.createdAt)} />
                  {/* Updated (read-only) */}
                  <MetadataField label="Updated" value={formatRelative(task.updatedAt)} />
                  {/* Parent (read-only, clickable) */}
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Parent</span>
                    {parentTask ? (
                      <button
                        type="button"
                        className="text-left text-sm text-blue-600 hover:underline dark:text-blue-400 truncate"
                        onClick={() => onNavigateToParent(parentTask._id)}
                        title={parentTask.title}
                      >
                        {parentTask.title}
                      </button>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Description (Markdown) ──────────────────────────────── */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    Description
                  </h3>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowDescriptionPreview(false)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                        !showDescriptionPreview ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Pencil className="h-3 w-3" /> Write
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDescriptionPreview(true)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                        showDescriptionPreview ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Eye className="h-3 w-3" /> Preview
                    </button>
                  </div>
                </div>
                {showDescriptionPreview ? (
                  <div
                    className="w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm leading-relaxed min-h-[4rem] prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: descriptionDraft
                        ? renderMarkdown(descriptionDraft)
                        : '<span class="text-muted-foreground">Nothing to preview</span>',
                    }}
                  />
                ) : (
                  <textarea
                    ref={textareaRef}
                    value={descriptionDraft}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Add a description... (Markdown supported)"
                    rows={3}
                    className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                )}
              </div>

              {/* ── AI Assignment Document (editable) ─────────────────────── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">AI Assignment Document</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowMarkdownPreview(false)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                        !showMarkdownPreview ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowMarkdownPreview(true)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                        showMarkdownPreview ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Eye className="h-3 w-3" /> Preview
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 ml-2"
                      onClick={() => {
                        navigator.clipboard.writeText(markdownDraft);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? (
                        <><Check className="h-3 w-3 text-green-500" /> Copied</>
                      ) : (
                        <><Copy className="h-3 w-3" /> Copy</>
                      )}
                    </Button>
                  </div>
                </div>
                {showMarkdownPreview ? (
                  <div
                    className="w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm leading-relaxed min-h-[8rem] max-h-96 overflow-y-auto prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: markdownDraft
                        ? renderMarkdown(markdownDraft)
                        : '<span class="text-muted-foreground">Nothing to preview</span>',
                    }}
                  />
                ) : (
                  <textarea
                    value={markdownDraft}
                    onChange={(e) => setMarkdownDraft(e.target.value)}
                    placeholder="Markdown document for AI agent assignment..."
                    rows={8}
                    className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-xs leading-relaxed font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring max-h-96"
                  />
                )}
              </div>

              {/* ── Progress (only if has children) ─────────────────────── */}
              {hasChildren && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-foreground">
                    Progress
                  </h3>
                  <div className="space-y-1.5">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-300',
                          progressPct === 100
                            ? 'bg-green-500 dark:bg-green-400'
                            : 'bg-blue-500 dark:bg-blue-400',
                        )}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {completedCount} of {children.length} completed ({progressPct}%)
                    </p>
                  </div>
                </div>
              )}

              {/* ── Subtasks ────────────────────────────────────────────── */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Subtasks
                  </h3>
                  {hasChildren && (
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {children.length}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-7 w-7"
                    onClick={() => onAddSubtask(task._id)}
                    aria-label="Add subtask"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {isLoadingChildren && (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner size="sm" />
                  </div>
                )}

                {isChildrenError && !isLoadingChildren && (
                  <p className="py-4 text-center text-sm text-destructive">
                    Failed to load subtasks.
                  </p>
                )}

                {!isLoadingChildren && !isChildrenError && !hasChildren && (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No subtasks
                  </p>
                )}

                {!isLoadingChildren && hasChildren && (
                  <ul className="space-y-1">
                    {children.map((child) => {
                      const childStatusCfg =
                        statusMap.get(child.status) ?? statusOptions[0];
                      return (
                        <li key={child._id}>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
                            onClick={() => onNavigateToParent(child._id)}
                          >
                            <span
                              className={cn(
                                'inline-block h-2 w-2 shrink-0 rounded-full',
                                childStatusCfg.dotClass,
                              )}
                            />
                            <span className="flex-1 truncate text-sm">
                              {child.title}
                            </span>
                            <span
                              className={cn(
                                'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                childStatusCfg.badgeClass,
                              )}
                            >
                              {childStatusCfg.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* ── Metadata JSON (editable, collapsible) ─────────────────── */}
              <div>
                <button
                  type="button"
                  className="mb-2 flex w-full items-center gap-1.5 text-sm font-semibold text-foreground hover:text-muted-foreground"
                  onClick={() => setMetadataExpanded((prev) => !prev)}
                >
                  {metadataExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Metadata
                </button>
                {metadataExpanded && (
                  <div className="space-y-1">
                    <textarea
                      value={metadataDraft}
                      onChange={(e) => {
                        setMetadataDraft(e.target.value);
                        setMetadataError(null);
                      }}
                      placeholder='{"key": "value"}'
                      rows={4}
                      className={cn(
                        'w-full resize-none rounded-md border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-ring',
                        metadataError ? 'border-red-500' : 'border-input',
                      )}
                    />
                    {metadataError && (
                      <p className="text-xs text-destructive">{metadataError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete confirmation dialog ──────────────────────────────────── */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete task"
        description={`Are you sure you want to delete "${task.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </>
  );
}

// ─── Metadata field helper ──────────────────────────────────────────────────

function MetadataField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="truncate text-sm text-foreground" title={value}>
        {value}
      </span>
    </div>
  );
}
